import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { StaffService } from "../../services/dataService";
import FullScreenLoading from "./FullScreenLoading";
import { motion } from "framer-motion";
import axios from "axios";
import { BASE_URL } from "../../services/configUrls";
import DashboardCard from "./StatisticsCard";
import FilterComponent from "./FilterComponent";
import api from "../../services/api";
import FullScreenDatabaseLoading from "./FullScreenDatabaseLoading";
import SubmissionSuccessModal from "./SubmissionSuccessModal";

// Add custom scrollbar styles
const scrollbarStyles = `
  /* Custom scrollbar styles */
  .custom-scrollbar::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #3b82f6;
    border-radius: 4px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #2563eb;
  }

  /* Required field styles */
  .required-field::after {
    content: " *";
    color: #ef4444;
  }

  /* Pre-filled cell styles */
  .prefilled-cell {
    background-color: #e0f2fe;
    position: relative;
  }

  .prefilled-cell:before {
    content: "Auto-filled";
    position: absolute;
    bottom: 0;
    right: 0;
    font-size: 9px;
    color: #3b82f6;
    padding: 0 2px;
    pointer-events: none;
  }

  .prefilled-header {
    background-color: #0369a1 !important;
  }

  /* Student type button styles */
  .student-type-btn {
    padding: 12px 32px;
    font-weight: 600;
    border-radius: 8px;
    transition: all 0.3s ease;
    border: 2px solid #0288d1;
    cursor: pointer;
  }

  .student-type-btn.active {
    background-color: #0288d1;
    color: white;
  }

  .student-type-btn:not(.active) {
    background-color: white;
    color: #0288d1;
  }

  .student-type-btn:not(.active):hover {
    background-color: #e3f2fd;
  }
`;

const NewMaterialExcelInterface = () => {
  const userRole = useSelector((state) => state.authorise.userRole);

  // Student type state
  const [studentType, setStudentType] = useState("indian");

  // Country state for foreign students
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCountryId, setSelectedCountryId] = useState(""); // NEW: Store country_id

  const HEADERS = ["Email"];
  const API_HEADERS = ["mail_id", "name", "gender", "branch", "year"];
  const MAX_BATCH_ROWS = 500;

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);

  const [databaseLimits, setDatabaseLimits] = useState({
    institute_id: null,
    is_student_addition:null,//srittam added
    source: null,
    student_intake_data: 0,
    registration_limit: null,
    deleted: 0,
    students_hold: 0,
    topup_limit: null,
    topup_used: 0,
    topup_limit_available: null,
    isLoading: true,
    error: null,
  });

  const [rows, setRows] = useState([[""], [""], [""]]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");

  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [branchDisabled, setBranchDisabled] = useState(true);
  const [selectedYear, setSelectedYear] = useState("");
  const [yearDisabled, setYearDisabled] = useState(true);
  const [filtersValid, setFiltersValid] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState("");

  const [history, setHistory] = useState([
    Array(3)
      .fill()
      .map(() => Array(HEADERS.length).fill("")),
  ]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [activeCell, setActiveCell] = useState({ row: -1, col: -1 });
  const tableRef = useRef(null);
  const cellRefs = useRef([]);
  const isLeader = userRole === "leaders";

  // Check if filters are valid
  useEffect(() => {
    if (studentType === "indian") {
      // For Indian students, only Course, Branch, and Year are required
      if (selectedCourse && selectedBranch && selectedYear) {
        setFiltersValid(true);
      } else {
        setFiltersValid(false);
      }
    } else {
      // For Foreign students, Course, Branch, and Year are required (Country is optional)
      if (selectedCourse && selectedBranch && selectedYear) {
        setFiltersValid(true);
      } else {
        setFiltersValid(false);
      }
    }
  }, [
    selectedCourse,
    selectedBranch,
    selectedYear,
    selectedCountry,
    studentType,
  ]);

  // Reset data when student type changes
  useEffect(() => {
    const initialRows = Array(3)
      .fill()
      .map(() => Array(HEADERS.length).fill(""));
    setRows(initialRows);
    setHistory([initialRows]);
    setHistoryIndex(0);
    setActiveCell({ row: -1, col: -1 });
    setSelectedCountry("");
    setSelectedCountryId(""); // NEW: Clear country ID
  }, [studentType]);

  const getRemainingSlots = () => {
    const {
      student_intake_data,
      registration_limit,
      students_hold,
      topup_limit_available,
    } = databaseLimits;

    if (registration_limit === null) return Infinity;
    if (registration_limit === 0) return 0;
    if (topup_limit_available !== null && topup_limit_available > 0) {
      return topup_limit_available;
    }
    const totalUsed = student_intake_data + students_hold;
    return Math.max(0, registration_limit - totalUsed);
  };

  const getMaxRows = () => {
    const remainingSlots = getRemainingSlots();
    if (remainingSlots === Infinity) return MAX_BATCH_ROWS;
    return Math.min(remainingSlots, MAX_BATCH_ROWS);
  };

  useEffect(() => {
    fetchDatabaseLimits();
  }, []);

  const fetchDatabaseLimits = async () => {
  try {
    setDatabaseLimits((prev) => ({ ...prev, isLoading: true }));
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) throw new Error("Access token not found");

    const response = await api.get(`${BASE_URL}/internship/databaseLimits`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    const instituteId = response.data.institute_id ?? null;
    const instituteSource = response.data.institute_source ?? null;

    // ✅ Set studentType immediately when institute_id is known
    if (instituteSource?.toLowerCase() === "foreign") {
  setStudentType("foreign");
}

    const topupLimit = response.data.topup_limit || null;
    const topupUsed = response.data.topup_used || 0;
    const topupAvailable =
      response.data.topup_limit_available !== undefined
        ? response.data.topup_limit_available
        : topupLimit !== null
          ? Math.max(0, topupLimit - topupUsed)
          : null;

    setDatabaseLimits({
      institute_id: instituteId,
      is_student_addition: response.data.is_student_addition,
      student_intake_data: response.data.student_intake_data || 0,
      registration_limit: response.data.registration_limit,
      deleted: response.data.deleted || 0,
      students_hold: response.data.students_hold || 0,
      source: instituteSource,
      topup_limit: topupLimit,
      topup_used: topupUsed,
      topups: response.data.topups || [],
      topup_limit_available: topupAvailable,
      isLoading: false,
      error: null,
    });
  } catch (error) {
    console.error("Error fetching database limits:", error);
    setDatabaseLimits((prev) => ({
      ...prev,
      isLoading: false,
      error: error.message || "Failed to fetch database limits",
    }));
  }
};

  useEffect(() => {
    cellRefs.current = Array(rows.length)
      .fill()
      .map(() =>
        Array(HEADERS.length)
          .fill()
          .map(() => React.createRef())
      );
  }, [rows.length]);

  const addToHistory = (newRows) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(newRows)));
    if (newHistory.length > 50) newHistory.shift();
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setRows(JSON.parse(JSON.stringify(history[newIndex])));
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setRows(JSON.parse(JSON.stringify(history[newIndex])));
    }
  };

  const isSpecialInstitute = databaseLimits.isLoading || databaseLimits.source?.toLowerCase() === "foreign";
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (isLoading || isSubmitting || isLeader) return;
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === "y" || (e.key === "z" && e.shiftKey))
      ) {
        e.preventDefault();
        redo();
      }
    };
    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, [historyIndex, history, isLoading, isSubmitting, isLeader]);

  const getDataStats = () => {
    let nonEmptyCells = 0;
    let nonEmptyRows = 0;
    for (const row of rows) {
      const hasContent = row.some((cell) => cell && cell.trim() !== "");
      if (hasContent) {
        nonEmptyRows++;
        for (const cell of row) {
          if (cell && cell.trim() !== "") nonEmptyCells++;
        }
      }
    }
    return {
      totalRows: rows.length,
      nonEmptyRows,
      nonEmptyCells,
      columnsCount: HEADERS.length,
    };
  };

  const handlePaste = (e) => {
    if (isLoading || isSubmitting || isLeader) return;
    if (activeCell.row === -1 || activeCell.col === -1) return;

    if (!filtersValid) {
      e.preventDefault();
      toast.error(
        "Please select Course, Branch, and Passout Year before adding data."
      );
      return;
    }

    if (getRemainingSlots() === 0) {
      e.preventDefault();
      const { students_hold, topup_limit_available } = databaseLimits;
      if (topup_limit_available === 0) {
        toast.error(
          "Both registration and topup limits have been reached. Please contact your RM."
        );
      } else if (students_hold > 0) {
        toast.error(
          `Registration limit reached. ${students_hold} students are on hold due to TDS. Please contact your RM.`
        );
      } else {
        toast.error("Registration limit reached. Please contact your RM.");
      }
      return;
    }

    e.preventDefault();
    setIsLoading(true);

    try {
      let pastedText = "";
      try {
        const clipboardData = e.clipboardData || window.clipboardData;
        if (clipboardData) {
          pastedText = clipboardData.getData("text") || "";
        }
      } catch (clipErr) {
        console.error("Clipboard access error:", clipErr);
      }

      if (!pastedText.trim()) {
        toast.warning("No content found in clipboard.");
        setIsLoading(false);
        return;
      }

      const pastedRows = pastedText
        .split(/\r\n|\n|\r/)
        .filter(
          (row) => row !== null && row !== undefined && row.trim() !== ""
        );

      const isLargePaste = pastedText.length > 10000 || pastedRows.length > 50;

      if (isLargePaste) {
        const estimatedCells = pastedRows.reduce((sum, row) => {
          const delimiter = row.includes("\t") ? "\t" : ",";
          return sum + row.split(delimiter).length;
        }, 0);

        toast.info(
          `Processing ${pastedRows.length} rows with approximately ${estimatedCells} cells, please wait...`
        );
        setLoadingMessage(`Processing ${pastedRows.length} rows...`);
      }

      setTimeout(() => {
        try {
          const pastedRows = pastedText
            .split(/\r\n|\n|\r/)
            .filter(
              (row) => row !== null && row !== undefined && row.trim() !== ""
            );

          if (pastedRows.length === 0) {
            toast.warning("No valid data rows found.");
            setIsLoading(false);
            return;
          }

          const pastedData = pastedRows.map((row) => {
            const delimiter = row.includes("\t") ? "\t" : ",";
            const cells = row.split(delimiter).map((cell) => (cell || "").trim());

            return [cells[0] || ""];
          });

          let dataToInsert = pastedData;

          if (pastedData.length > 0 && pastedData[0].length > 0) {
            const possibleHeaders = pastedData[0].map((h) =>
              (h || "").toString().toLowerCase()
            );
            const knownHeaders = HEADERS.map((h) => h.toLowerCase());

            if (possibleHeaders.some((h) => knownHeaders.includes(h))) {
              dataToInsert = pastedData.slice(1);
            }
          }

          const newRows = [...rows];
          const maxRowsAllowed = getMaxRows();

          const potentialRowCount = Math.max(
            newRows.length,
            activeCell.row + dataToInsert.length
          );

          if (potentialRowCount > maxRowsAllowed) {
            const rowsAvailable = maxRowsAllowed - activeCell.row;
            if (rowsAvailable <= 0) {
              toast.error(
                `Row limit of ${maxRowsAllowed} reached. Cannot paste at the current position.`
              );
              setIsLoading(false);
              return;
            }

            toast.warning(
              `Row limit of ${maxRowsAllowed} reached. Only pasting ${rowsAvailable} of ${dataToInsert.length} rows.`
            );
            dataToInsert = dataToInsert.slice(0, rowsAvailable);
          }

          let rowsAdded = 0;

          for (let i = 0; i < dataToInsert.length; i++) {
            const targetRowIndex = activeCell.row + i;

            if (targetRowIndex >= newRows.length) {
              newRows.push(Array(HEADERS.length).fill(""));
              rowsAdded++;
            }

            for (
              let j = 0;
              j < dataToInsert[i].length && j + activeCell.col < HEADERS.length;
              j++
            ) {
              const cellValue = dataToInsert[i][j];
              newRows[targetRowIndex][activeCell.col + j] =
                cellValue !== null && cellValue !== undefined
                  ? cellValue.toString()
                  : "";
            }
          }

          if (dataToInsert.length > 0) {
            setRows(newRows);
            addToHistory(newRows);

            const totalCellsPasted = dataToInsert.reduce(
              (sum, row) =>
                sum + Math.min(row.length, HEADERS.length - activeCell.col),
              0
            );

            toast.success(
              `Pasted ${dataToInsert.length
              } rows and ${totalCellsPasted} cells${rowsAdded > 0 ? ` (added ${rowsAdded} new rows)` : ""
              }`
            );
          } else {
            toast.warning(
              "No data was pasted. Please check your clipboard content."
            );
          }
        } catch (err) {
          console.error("Paste processing error:", err);
          toast.error(
            `Error processing pasted data: ${err.message || "Unknown error"}`
          );
        } finally {
          setIsLoading(false);
        }
      }, 100);
    } catch (err) {
      console.error("Initial paste error:", err);
      toast.error(`Clipboard error: ${err.message || "Unknown error"}`);
      setIsLoading(false);
    }
  };

  const handleCellClick = (rowIndex, colIndex) => {
    if (isLeader) {
      toast.warning("You do not have permission to edit this data.");
      return;
    }

    if (!filtersValid) {
      toast.error(
        "Please select Course, Branch, and Passout Year before adding data."
      );
      return;
    }

    if (getRemainingSlots() === 0) {
      const { students_hold, topup_limit_available } = databaseLimits;
      if (topup_limit_available === 0) {
        toast.error(
          "Both registration and topup limits have been reached. Please contact your RM."
        );
      } else if (students_hold > 0) {
        toast.error(
          `Registration limit reached. ${students_hold} students are on hold due to TDS. Please contact your RM.`
        );
      } else {
        toast.error("Registration limit reached. Please contact your RM.");
      }
      return;
    }

    setActiveCell({ row: rowIndex, col: colIndex });
  };

  const handleCellChange = (rowIndex, colIndex, value) => {
    if (isLeader) return;
    if (!filtersValid) return;
    if (getRemainingSlots() === 0) return;

    const newRows = [...rows];
    if (newRows[rowIndex][colIndex] !== value) {
      newRows[rowIndex][colIndex] = value;
      setRows(newRows);
      addToHistory(newRows);
    }
  };

  const addRow = () => {
    if (isLeader) {
      toast.warning("You do not have permission to add rows.");
      return;
    }

    if (!filtersValid) {
      toast.error(
        "Please select Course, Branch, and Passout Year before adding data."
      );
      return;
    }

    if (getRemainingSlots() === 0) {
      const { students_hold, topup_limit_available } = databaseLimits;
      if (topup_limit_available === 0 && students_hold > 0) {
        toast.error(
          `Registration limit reached. ${students_hold} students are on hold due to TDS. Please contact your RM.`
        );
      } else {
        toast.error("Registration limit reached. Please contact your RM.");
      }
      return;
    }

    const maxRowsAllowed = getMaxRows();
    if (rows.length >= maxRowsAllowed) {
      toast.error(`Maximum row limit of ${maxRowsAllowed} reached.`);
      return;
    }

    const newRows = [...rows, Array(HEADERS.length).fill("")];
    setRows(newRows);
    addToHistory(newRows);
  };

  const deleteRow = () => {
    if (isLeader || activeCell.row === -1 || rows.length <= 1) return;

    const newRows = [...rows];
    newRows.splice(activeCell.row, 1);
    setRows(newRows);
    addToHistory(newRows);
    setActiveCell({ row: -1, col: -1 });
  };

  const clearData = () => {
    if (isLeader) {
      toast.warning("You do not have permission to clear data.");
      return;
    }

    const newRows = Array(3)
      .fill()
      .map(() => Array(HEADERS.length).fill(""));

    setRows(newRows);
    addToHistory(newRows);
    setActiveCell({ row: -1, col: -1 });
  };

  // UPDATED: convertToCSV function to send country_id instead of country name
  const convertToCSV = (dataRows) => {
    const nonEmptyRows = dataRows.filter((row) =>
      row.some((cell) => cell.trim() !== "")
    );

    const rowsWithFilters = nonEmptyRows.map((row) => {
      return [
        row[0].trim(), // email
        "", // name (empty)
        "", // gender (empty)
        selectedBranch,
        selectedYear,
        selectedCourseId,
        studentType === "foreign" ? selectedCountryId : "", // Send country_id for foreign, empty for Indian
      ];
    });

    const API_HEADERS_WITH_COUNTRY = [
      "mail_id",
      "name",
      "gender",
      "branch",
      "year",
      "program_id",
      "country_id", // This contains country_id for foreign students
    ];

    return [
      API_HEADERS_WITH_COUNTRY.join(","),
      ...rowsWithFilters.map((row) =>
        row
          .map((cell) => {
            if (
              cell &&
              (cell.toString().includes(",") || cell.toString().includes('"'))
            ) {
              return `"${cell.toString().replace(/"/g, '""')}"`;
            }
            return cell || "";
          })
          .join(",")
      ),
    ].join("\n");
  };

  // UPDATED: handleSubmit with direct API call
  const handleSubmit = async () => {
    if (isLeader) {
      toast.warning("You do not have permission to submit data.");
      return;
    }

    if (!filtersValid) {
      toast.error(
        "Please select Course, Branch, and Passout Year before submitting."
      );
      return;
    }

    if (getRemainingSlots() === 0) {
      const { students_hold, topup_limit_available } = databaseLimits;
      if (topup_limit_available === 0 && students_hold > 0) {
        toast.error(
          `Registration limit reached. ${students_hold} students are on hold due to TDS. Please contact your RM.`
        );
      } else {
        toast.error("Registration limit reached. Please contact your RM.");
      }
      return;
    }

    setIsSubmitting(true);
    setLoadingMessage("Submitting your data...");

    try {
      const filteredRows = rows.filter((row) =>
        row.some((cell) => cell.trim() !== "")
      );

      if (filteredRows.length === 0) {
        toast.warning("No data to submit. Please add some data first.");
        setIsSubmitting(false);
        return;
      }

      if (filteredRows.length > getRemainingSlots()) {
        toast.error(
          `You can only submit up to ${getRemainingSlots()} rows of data.`
        );
        setIsSubmitting(false);
        return;
      }

      const csvData = convertToCSV(filteredRows);
      console.log("Submitting CSV data:", csvData);

      const csvBlob = new Blob([csvData], { type: "text/csv" });
      const csvFile = new File([csvBlob], "intake_data.csv", {
        type: "text/csv",
      });

      const formData = new FormData();
      formData.append("file", csvFile);

      // NEW: Get access token
      const token = localStorage.getItem("accessToken");

      if (!token) {
        throw new Error("Access token not found");
      }

      // NEW: Determine URL based on student type
      // 0 = foreign students, 1 = Indian students
      const studentTypeParam = studentType === "foreign" ? "0" : "1";
      const endpoint = `${BASE_URL}/profile/studentIntake/${studentTypeParam}`;

      console.log("Submitting to endpoint:", endpoint);

      // NEW: Direct API call
      const response = await api.post(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("Success:", response);

      setSubmissionResult(response?.data);
      setShowSuccessModal(true);

      await refreshDataAfterSubmission();
    } catch (error) {
      console.error("Error submitting data:", error);
      toast.error(
        error?.response?.data?.detail ||
        "Error submitting data. Please try again."
      );
      setIsSubmitting(false);
    }
  };

  // UPDATED: refreshDataAfterSubmission to clear country ID
  const refreshDataAfterSubmission = async () => {
    try {
      setLoadingMessage("Refreshing data...");
      await fetchDatabaseLimits();

      const initialRows = Array(3)
        .fill()
        .map(() => Array(HEADERS.length).fill(""));

      setRows(initialRows);
      setHistory([initialRows]);
      setHistoryIndex(0);
      setActiveCell({ row: -1, col: -1 });

      setSelectedCourse("");
      setSelectedBranch("");
      setSelectedYear("");
      setSelectedCountry("");
      setSelectedCountryId(""); // NEW: Clear country ID
      setBranchDisabled(true);
      setYearDisabled(true);
      setFiltersValid(false);
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast.warning(
        "Data submitted successfully, but failed to refresh. Please refresh the page manually."
      );
    } finally {
      setIsSubmitting(false);
      setLoadingMessage("");
    }
  };

  const handleCopy = () => {
    if (isLeader || activeCell.row === -1 || activeCell.col === -1) return;

    try {
      const cell = cellRefs.current[activeCell.row][activeCell.col].current;
      if (!cell) return;

      const cellText = cell.textContent || "";

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(cellText).catch((err) => {
          console.error("Clipboard write failed:", err);
          fallbackCopy(cellText);
        });
      } else {
        fallbackCopy(cellText);
      }
    } catch (err) {
      console.error("Copy error:", err);
    }
  };

  const fallbackCopy = (text) => {
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand("copy");
      } catch (err) {
        console.error("execCommand copy failed:", err);
      }

      document.body.removeChild(textArea);
    } catch (err) {
      console.error("Fallback copy error:", err);
    }
  };

  const handleCut = () => {
    if (
      isLeader ||
      isLoading ||
      isSubmitting ||
      activeCell.row === -1 ||
      activeCell.col === -1
    )
      return;

    try {
      const cell = cellRefs.current[activeCell.row][activeCell.col].current;
      if (!cell) return;

      const cellText = cell.textContent || "";

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(cellText)
          .then(() => {
            const newRows = [...rows];
            newRows[activeCell.row][activeCell.col] = "";
            setRows(newRows);
            addToHistory(newRows);
            cell.textContent = "";
          })
          .catch((err) => {
            console.error("Clipboard write failed during cut:", err);
            fallbackCopy(cellText);
            const newRows = [...rows];
            newRows[activeCell.row][activeCell.col] = "";
            setRows(newRows);
            addToHistory(newRows);
            cell.textContent = "";
          });
      } else {
        fallbackCopy(cellText);
        const newRows = [...rows];
        newRows[activeCell.row][activeCell.col] = "";
        setRows(newRows);
        addToHistory(newRows);
        cell.textContent = "";
      }
    } catch (err) {
      console.error("Cut error:", err);
    }
  };

  const handleKeyDown = (e, rowIndex, colIndex) => {
    if (isLeader && !["Escape", "Tab"].includes(e.key)) {
      e.preventDefault();
      return;
    }

    if (!filtersValid && !["Escape", "Tab"].includes(e.key)) {
      e.preventDefault();
      return;
    }

    if (getRemainingSlots() === 0 && !["Escape", "Tab"].includes(e.key)) {
      e.preventDefault();
      return;
    }

    if (
      (isLoading || isSubmitting) &&
      !["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Tab"].includes(
        e.key
      )
    )
      return;

    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case "c":
          e.preventDefault();
          handleCopy();
          return;
        case "x":
          e.preventDefault();
          handleCut();
          return;
        case "v":
          return;
        default:
          break;
      }
    }

    const maxRowsAllowed = getMaxRows();

    switch (e.key) {
      case "ArrowUp":
        if (rowIndex > 0) {
          e.preventDefault();
          setActiveCell({ row: rowIndex - 1, col: colIndex });
          cellRefs.current[rowIndex - 1][colIndex].current?.focus();
        }
        break;
      case "ArrowDown":
        if (rowIndex < rows.length - 1) {
          e.preventDefault();
          setActiveCell({ row: rowIndex + 1, col: colIndex });
          cellRefs.current[rowIndex + 1][colIndex].current?.focus();
        } else if (
          rowIndex === rows.length - 1 &&
          rows.length < maxRowsAllowed &&
          !isLeader
        ) {
          e.preventDefault();
          addRow();
          setTimeout(() => {
            setActiveCell({ row: rows.length, col: colIndex });
            cellRefs.current[rows.length]?.[colIndex]?.current?.focus();
          }, 0);
        } else if (
          rowIndex === rows.length - 1 &&
          rows.length >= maxRowsAllowed
        ) {
          toast.warning(`Row limit of ${maxRowsAllowed} reached.`);
        }
        break;
      case "ArrowLeft":
        if (colIndex > 0) {
          e.preventDefault();
          setActiveCell({ row: rowIndex, col: colIndex - 1 });
          cellRefs.current[rowIndex][colIndex - 1].current?.focus();
        }
        break;
      case "ArrowRight":
        if (colIndex < HEADERS.length - 1) {
          e.preventDefault();
          setActiveCell({ row: rowIndex, col: colIndex + 1 });
          cellRefs.current[rowIndex][colIndex + 1].current?.focus();
        }
        break;
      case "Tab":
        e.preventDefault();
        if (e.shiftKey) {
          if (rowIndex > 0) {
            setActiveCell({ row: rowIndex - 1, col: colIndex });
            cellRefs.current[rowIndex - 1][colIndex].current?.focus();
          }
        } else {
          if (rowIndex < rows.length - 1) {
            setActiveCell({ row: rowIndex + 1, col: colIndex });
            cellRefs.current[rowIndex + 1][colIndex].current?.focus();
          } else if (rows.length < maxRowsAllowed && !isLeader) {
            addRow();
            setTimeout(() => {
              setActiveCell({ row: rows.length, col: colIndex });
              cellRefs.current[rows.length]?.[colIndex]?.current?.focus();
            }, 0);
          } else {
            toast.warning(`Row limit of ${maxRowsAllowed} reached.`);
          }
        }
        break;
      case "Enter":
        if (!e.shiftKey) {
          e.preventDefault();
          if (rowIndex < rows.length - 1) {
            setActiveCell({ row: rowIndex + 1, col: colIndex });
            cellRefs.current[rowIndex + 1][colIndex].current?.focus();
          } else if (rows.length < maxRowsAllowed && !isLeader) {
            addRow();
            setTimeout(() => {
              setActiveCell({ row: rows.length, col: colIndex });
              cellRefs.current[rows.length]?.[colIndex]?.current?.focus();
            }, 0);
          } else if (
            rowIndex === rows.length - 1 &&
            rows.length >= maxRowsAllowed
          ) {
            toast.warning(`Row limit of ${maxRowsAllowed} reached.`);
          }
        }
        break;
      default:
        break;
    }
  };

  const getButtonClass = (variant = "default", disabled = false) => {
    const baseClasses =
      "px-2 py-1 rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-opacity-50 text-xs";

    if (disabled) {
      return `${baseClasses} bg-gray-200 text-gray-500 cursor-not-allowed`;
    }

    switch (variant) {
      case "contained":
        return `${baseClasses} bg-blue-700 hover:bg-blue-800 text-white focus:ring-blue-500`;
      case "outlined":
        return `${baseClasses} border border-blue-500 hover:bg-blue-50 text-blue-700 focus:ring-blue-500`;
      case "text":
        return `${baseClasses} text-blue-700 hover:bg-blue-50 focus:ring-blue-500`;
      case "error":
        return `${baseClasses} bg-red-600 hover:bg-red-700 text-white focus:ring-red-500`;
      case "warning":
        return `${baseClasses} bg-amber-500 hover:bg-amber-600 text-white focus:ring-amber-400`;
      default:
        return `${baseClasses} bg-blue-700 hover:bg-blue-800 text-white focus:ring-blue-500`;
    }
  };

  const canAddData = () => {
    if (databaseLimits.isLoading) return true;
    return getRemainingSlots() > 0 && !isLeader;
  };

  return (
    <div className="w-full mx-auto p-4 relative">
      <style>{scrollbarStyles}</style>

      <FullScreenDatabaseLoading
        isVisible={databaseLimits.isLoading}
        message="Loading Student Limits"
      />

      <SubmissionSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        submissionData={submissionResult}
      />

      {/* Registration Limits Dashboard */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-800 mb-3 text-left">
          Student Limits
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {databaseLimits.registration_limit !== null && (
            <DashboardCard
              title="Approved"
              value={databaseLimits.registration_limit}
              showInfinitySymbol={databaseLimits.registration_limit === null}
              icon="approved"
              theme="green"
              isLoading={databaseLimits.isLoading}
              error={databaseLimits.error}
            />
          )}

          <DashboardCard
            title="Used"
            value={databaseLimits.student_intake_data}
            icon="students"
            theme="purple"
            isLoading={databaseLimits.isLoading}
            error={databaseLimits.error}
          />

          {databaseLimits.registration_limit !== null && (
            <DashboardCard
              title={
                databaseLimits.topup_limit_available !== null &&
                  databaseLimits.topup_limit_available > 0
                  ? "Available"
                  : getRemainingSlots() === 0
                    ? "Limit Reached"
                    : "Available"
              }
              value={
                databaseLimits.registration_limit === null
                  ? null
                  : Math.max(
                    0,
                    databaseLimits.registration_limit -
                    (databaseLimits.student_intake_data +
                      databaseLimits.students_hold)
                  )
              }
              showInfinitySymbol={databaseLimits.registration_limit === null}
              additionalInfo={
                databaseLimits.students_hold > 0 &&
                  Math.max(
                    0,
                    databaseLimits.registration_limit -
                    (databaseLimits.student_intake_data +
                      databaseLimits.students_hold)
                  ) === 0
                  ? `${databaseLimits.students_hold} on TDS hold`
                  : null
              }
              icon={
                Math.max(
                  0,
                  databaseLimits.registration_limit -
                  (databaseLimits.student_intake_data +
                    databaseLimits.students_hold)
                ) === 0
                  ? "warning"
                  : "task"
              }
              theme="indigo"
              usedValue={
                databaseLimits.student_intake_data +
                databaseLimits.students_hold
              }
              totalValue={databaseLimits.registration_limit}
              percentage={
                databaseLimits.registration_limit === null
                  ? null
                  : Math.min(
                    100,
                    Math.round(
                      ((databaseLimits.student_intake_data +
                        databaseLimits.students_hold) /
                        databaseLimits.registration_limit) *
                      100
                    )
                  )
              }
              isLoading={databaseLimits.isLoading}
              error={databaseLimits.error}
            />
          )}

          <DashboardCard
            title="Deleted"
            value={databaseLimits.deleted}
            icon="deleted"
            theme="rose"
            isLoading={databaseLimits.isLoading}
            error={databaseLimits.error}
          />

          {databaseLimits.topup_limit !== null && (
            <DashboardCard
              title="Student Topup"
              value={databaseLimits.topup_used}
              icon="zap"
              theme="amber"
              usedValue={databaseLimits.topup_used}
              totalValue={databaseLimits.topup_limit}
              percentage={
                databaseLimits.topup_limit > 0
                  ? Math.min(
                    100,
                    Math.round(
                      (databaseLimits.topup_used /
                        databaseLimits.topup_limit) *
                      100
                    )
                  )
                  : null
              }
              topupData={databaseLimits.topups || []}
              isLoading={databaseLimits.isLoading}
              error={databaseLimits.error}
            />
          )}

          {databaseLimits.students_hold > 0 && (
            <DashboardCard
              title="TDS Hold"
              value={databaseLimits.students_hold}
              icon="warning"
              theme="amber"
              isLoading={databaseLimits.isLoading}
              error={databaseLimits.error}
            />
          )}
        </div>
      </div>

      {/* Student Type Selection - Outside the card */}
      {!isSpecialInstitute && (
  <div className="mb-6">
    <h3 className="text-lg font-medium text-gray-800 mb-3 text-left">
      Select Student Type
    </h3>
    <div className="flex gap-4">
      <button
        className={`student-type-btn ${studentType === "indian" ? "active" : ""}`}
        onClick={() => setStudentType("indian")}
        disabled={isLoading || isSubmitting}
      >
        Indian Students
      </button>
      <button
        className={`student-type-btn ${studentType === "foreign" ? "active" : ""}`}
        onClick={() => setStudentType("foreign")}
        disabled={isLoading || isSubmitting}
      >
        Foreign Students
      </button>
    </div>
  </div>
)}

      <FullScreenLoading isVisible={isSubmitting} message={loadingMessage} />

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-4 bg-[#0288d1] border-b border-blue-800">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-medium text-white">Add Students</h2>
              <p className="text-sm text-blue-100 mt-1">
                Enter email data or paste directly from Excel.
              </p>
            </div>
          </div>

          {isLeader ? (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-300 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 text-amber-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-bold text-amber-800">
                    View Only Access
                  </h3>
                  <p className="text-amber-700 mt-1">
                    You have read-only access to this section. Data modification
                    is restricted for your role.
                  </p>
                </div>
              </div>
            </div>
          ) : databaseLimits.is_student_addition===0?(
            <div className="mt-4 p-4 bg-red-50 border border-red-300 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 text-red-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-bold text-red-800">
                    Student addition is closed
                  </h3>
                  <p className="text-red-700 mt-1">
                    Please contact your Relationship Manager for assistance.
                  </p>
                </div>
              </div>
            </div>
          ) : !canAddData() && databaseLimits.registration_limit !== 0 ? (
            <div className="mt-4 p-4 bg-red-50 border border-red-300 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 text-red-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-bold text-red-800">
                    Registration Limit Reached
                  </h3>
                  <p className="text-red-700 mt-1">
                    {databaseLimits.students_hold > 0
                      ? `You have reached your student registration limit. ${databaseLimits.students_hold} students are on hold due to TDS. Please contact your Relationship Manager for assistance.`
                      : "You have reached your student registration limit. Please contact your Relationship Manager for assistance."}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* NEW: Pass country ID props to FilterComponent */}
              <FilterComponent
                selectedCourse={selectedCourse}
                setSelectedCourse={setSelectedCourse}
                selectedCourseId={selectedCourseId}
                setSelectedCourseId={setSelectedCourseId}
                selectedBranch={selectedBranch}
                setSelectedBranch={setSelectedBranch}
                selectedYear={selectedYear}
                setSelectedYear={setSelectedYear}
                branchDisabled={branchDisabled}
                setBranchDisabled={setBranchDisabled}
                yearDisabled={yearDisabled}
                setYearDisabled={setYearDisabled}
                filtersValid={filtersValid}
                isLoading={isLoading}
                isSubmitting={isSubmitting}
                canAddData={canAddData()}
                isLeader={isLeader}
                studentType={studentType}
                selectedCountry={selectedCountry}
                setSelectedCountry={setSelectedCountry}
                selectedCountryId={selectedCountryId} // NEW
                setSelectedCountryId={setSelectedCountryId} // NEW
              />
            </>
          )}

          {canAddData() && !isLeader && (
            <div className="mt-4 p-2 bg-blue-50 border border-blue-200 rounded-md flex justify-between items-center">
              <div className="text-sm flex space-x-4">
                {filtersValid && !databaseLimits.isLoading && (
                  <span className="font-semibold text-blue-800">
                    Current Rows: {getDataStats().totalRows}
                  </span>
                )}
                <span className="font-medium text-gray-600">
                  (Max {MAX_BATCH_ROWS} rows are allowed)
                </span>
                {databaseLimits.topup_limit_available !== null &&
                  databaseLimits.topup_limit_available > 0 && (
                    <span className="font-medium text-green-600">
                      ({databaseLimits.topup_limit_available} topup slots
                      available)
                    </span>
                  )}
                {databaseLimits.topup_limit_available === 0 && (
                  <span className="font-medium text-amber-600">
                    (Topup limit exhausted)
                  </span>
                )}
                {databaseLimits.students_hold > 0 && (
                  <span className="font-medium text-amber-600">
                    ({databaseLimits.students_hold} students on TDS hold)
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Content with Custom Scrollbar */}
        {canAddData() && !isLeader ? (
          <div
            className="px-6 py-4 h-96 overflow-y-auto custom-scrollbar relative"
            onPaste={handlePaste}
          >
            {!filtersValid &&
              !databaseLimits.isLoading &&
              canAddData() &&
              !isLeader && (
                <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-35">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 max-w-md text-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-16 w-16 text-amber-500 mx-auto mb-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    <h3 className="text-xl font-bold text-amber-800 mb-2">
                      Set Required Filters
                    </h3>
                    <p className="text-amber-700 mb-4">
                      {studentType === "indian"
                        ? "Please select Course, Branch, and Passout Year to continue."
                        : "Please select Country, Course, Branch, and Passout Year to continue."}
                    </p>
                  </div>
                </div>
              )}

            {isLoading && (
              <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-30">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  <p className="mt-2 text-blue-700 font-medium">
                    {loadingMessage || "Processing..."}
                  </p>
                </div>
              </div>
            )}

            <div className="overflow-x-auto custom-scrollbar border border-gray-300 rounded-md shadow-sm">
              <table className="w-full border-collapse" ref={tableRef}>
                <thead className="sticky top-0 z-10">
                  <tr className="bg-[#0288d1] text-white border-b border-blue-700">
                    {HEADERS.map((header, index) => (
                      <th
                        key={index}
                        className="px-4 py-3 text-left font-medium border-r border-blue-500 last:border-r-0"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className={`${rowIndex % 2 === 0 ? "bg-white" : "bg-blue-50"
                        } hover:bg-blue-100 ${activeCell.row === rowIndex ? "bg-blue-100" : ""
                        }`}
                    >
                      {row.map((cell, colIndex) => (
                        <td
                          key={colIndex}
                          className={`px-4 py-2 border-r border-b border-gray-300 last:border-r-0 ${activeCell.row === rowIndex &&
                            activeCell.col === colIndex
                            ? "bg-blue-200"
                            : ""
                            }`}
                          style={{ outline: "none" }}
                        >
                          <div
                            ref={cellRefs.current[rowIndex]?.[colIndex] ?? null}
                            contentEditable={
                              canAddData() &&
                              filtersValid &&
                              !isLoading &&
                              !isSubmitting &&
                              !isLeader
                            }
                            suppressContentEditableWarning={true}
                            className={`outline-none min-h-8 focus:outline-none ${!canAddData() || !filtersValid || isLeader
                              ? "opacity-70"
                              : ""
                              }`}
                            onClick={() => handleCellClick(rowIndex, colIndex)}
                            onFocus={() => handleCellClick(rowIndex, colIndex)}
                            onBlur={(e) =>
                              handleCellChange(
                                rowIndex,
                                colIndex,
                                e.target.textContent
                              )
                            }
                            onKeyDown={(e) =>
                              handleKeyDown(e, rowIndex, colIndex)
                            }
                            tabIndex={
                              !canAddData() || !filtersValid || isLeader
                                ? -1
                                : 0
                            }
                            data-row={rowIndex}
                            data-col={colIndex}
                          >
                            {cell}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Tips */}
            <div className="mt-4 text-sm text-gray-600">
              <p className="font-medium">Tips:</p>
              <ul className="mt-2 space-y-1 text-gray-600 ml-5 list-disc">
                <li>
                  Select student type: Indian Students or Foreign Students
                </li>
                {studentType === "foreign" && (
                  <li>First select Country from the dropdown</li>
                )}
                <li>
                  Then select required filters: Course → Branch → Passout Year
                </li>
                <li>Click on any cell to edit or select it</li>
                <li>Press Tab or Enter to move between rows</li>
                <li>
                  Select a cell and paste (Ctrl+V/Cmd+V) to import email
                  addresses
                </li>
                <li>Use Ctrl+Z/Cmd+Z to undo and Ctrl+Y/Cmd+Y to redo</li>
                <li>
                  Use Ctrl+C/Cmd+C to copy and Ctrl+X/Cmd+X to cut cell content
                </li>
                <li>Headers are fixed and won't be overwritten when pasting</li>
                <li>Use arrow keys to navigate between cells</li>
                <li className="text-blue-700 font-medium">
                  Branch and Passout Year will be automatically applied to all
                  students
                </li>
                {studentType === "foreign" && (
                  <li className="text-blue-700 font-medium">
                    Selected country will be automatically applied to all
                    students
                  </li>
                )}
                <li className="text-blue-700 font-medium">
                  Only email addresses are required - names will be set
                  automatically
                </li>
              </ul>
            </div>
          </div>
        ) : null}

        {/* Fixed Submit Button Footer */}
        {canAddData() && !isLeader && (
          <div className="px-6 py-4 bg-blue-50 border-t border-blue-100 flex justify-between space-x-2 sticky bottom-0 left-0 right-0 shadow-md z-10">
            <div>
              {filtersValid && canAddData() && !isLeader && (
                <span className="text-xs text-blue-800 bg-blue-100 px-2 py-1 rounded-md border border-blue-200">
                  Adding{" "}
                  {studentType === "indian"
                    ? "Indian"
                    : `Foreign (${selectedCountry || "Not Specified"})`}{" "}
                  students: {selectedCourse} / {selectedBranch} / {selectedYear}
                  {databaseLimits.topup_limit_available > 0 && (
                    <span className="ml-2 text-green-700 bg-green-100 px-1 py-0.5 rounded">
                      Topup: {databaseLimits.topup_limit_available} slots
                    </span>
                  )}
                </span>
              )}
              {!canAddData() && databaseLimits.registration_limit !== 0 && (
                <span className="text-xs text-red-800 bg-red-100 px-2 py-1 rounded-md border border-red-200">
                  {databaseLimits.students_hold > 0
                    ? `Registration limit reached. ${databaseLimits.students_hold} students on TDS hold. Contact your RM.`
                    : "Registration limit reached. Contact your RM for assistance."}
                </span>
              )}
              {isLeader && (
                <span className="text-xs text-amber-800 bg-amber-100 px-2 py-1 rounded-md border border-amber-200">
                  View-only access - Data modification is restricted for your
                  role.
                </span>
              )}
            </div>
            <div className="flex space-x-2">
              <motion.button
                className={getButtonClass(
                  "outlined",
                  isLoading ||
                  isSubmitting ||
                  !canAddData() ||
                  !filtersValid ||
                  isLeader
                )}
                onClick={clearData}
                disabled={
                  isLoading ||
                  isSubmitting ||
                  !canAddData() ||
                  !filtersValid ||
                  isLeader
                }
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                Reset Data
              </motion.button>
              <motion.button
                className={getButtonClass(
                  "contained",
                  isLoading ||
                  isSubmitting ||
                  !canAddData() ||
                  !filtersValid ||
                  isLeader
                )}
                onClick={handleSubmit}
                disabled={
                  isLoading ||
                  isSubmitting ||
                  !canAddData() ||
                  !filtersValid ||
                  isLeader
                }
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                Submit Data
              </motion.button>
            </div>
          </div>
        )}
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default NewMaterialExcelInterface;
